import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { MessageService, SelectItem, SelectItemGroup, TreeNode } from "primeng/api";
import { Message } from "@bbwt/classes";
import { QuerySourceService } from "../../api/query-source.service";
import { IQuerySource, QueryAccessLevel, QueryFilterMode } from "../../core/reporting-models";
import { QuerySourceNodeType } from "../query-builder-models";
import { QueryPreviewComponent } from "@main/reporting.v3/query-preview/components/query-preview.component";
import { QueryBuilderSqlEditorComponent } from "./query-builder-sql-editor.component";
import { IHttpResponseHandlerSettings } from "@bbwt/modules/data-service";
import { IOrganization, OrganizationService } from "@main/companies";
import { UserHelperService } from "@main/users/user-helper.service";
import { UserLogin, UserService } from "@main/users";
import { BehaviorSubject } from "rxjs";


@Component({
    selector: "query-builder",
    templateUrl: "./query-builder.component.html",
    styleUrls: ["query-builder.component.scss"]
})
export class QueryBuilderComponent implements OnInit {
    // Query source settings.
    protected querySources: IQuerySource[];
    protected querySourceOptionsTree: TreeNode<string>[] = [];

    // General settings.
    protected queryNameValidation: RegExp = /.*\S.*/;
    protected querySourceOptionSelected: string = null;

    // Dialog settings.
    protected querySourceDialogVisible: boolean = false;
    protected queryCreationDialogVisible: boolean = false;

    // Query publishing options.
    protected organizations: IOrganization[] = [];
    protected organizationOptions: SelectItem[];

    // Query filter mode options
    protected queryFilterModeOptions: SelectItem[] = [
        {
            icon: "groups",
            label: "Filter by this user organisation",
            value: QueryFilterMode.UserOrganizationFilter,
            title: "Apply organisation filtering based on user's primary organisation"
        },
        {
            icon: "filter_alt_off",
            label: "No organisation filtering",
            value: QueryFilterMode.NoFilter,
            title: "Disable automatic organisation filtering (Admin only)"
        }
    ];

    // Component fields.
    private _querySourceOptions: SelectItemGroup[];
    private _queryAccessLevel: QueryAccessLevel = QueryAccessLevel.Local;
    private _querySource: IQuerySource = {
        queryType: "sql",
        filterMode: QueryFilterMode.UserOrganizationFilter
    } as IQuerySource;

    private originalFilterMode: QueryFilterMode;

    @Input() processingQuery: boolean;
    @Input() standalone: boolean = false;
    @Output() readonly querySourceIdChange: EventEmitter<string> = new EventEmitter<string>();

    @ViewChild(QueryBuilderSqlEditorComponent) queryEditor: QueryBuilderSqlEditorComponent;
    @ViewChild(QueryPreviewComponent) queryPreview: QueryPreviewComponent;

    constructor(private querySourceService: QuerySourceService,
                private organizationService: OrganizationService,
                private userHelperService: UserHelperService,
                private messageService: MessageService,
                private userService: UserService) {
    }

    @Input() set querySourceId(value: string) {
        if (this.querySourceId === value) return;
        // For the moment being only SQL queries are supported, so query type is fixed to "sql".
        this._querySource = { ...this.querySource, queryType: "sql", id: value ?? undefined };
    }

    get querySourceId(): string {
        return this.querySource.id;
    }

    @Input() set queryAccessLevel(value: QueryAccessLevel) {
        if (this.queryAccessLevel === value) return;
        this._querySource = { ...this.querySource, level: value };
    }

    get queryAccessLevel(): QueryAccessLevel {
        return this._queryAccessLevel;
    }

    @Input() set querySourceOptions(value: SelectItemGroup[]) {
        if (!value) return;
        this._querySourceOptions = value;
        this.refreshQuerySourceOptions();
    }

    get querySourceOptions(): SelectItemGroup[] {
        return this._querySourceOptions;
    }

    set querySource(value: IQuerySource) {
        if (!value) return;
        this._querySource = value;
        this._querySource.level ??= this._queryAccessLevel;
        this._querySource.filterMode ??= QueryFilterMode.UserOrganizationFilter;
        this.querySourceIdChange.emit(this.querySourceId);
        this.refreshQuerySources();
    }

    get querySource(): IQuerySource {
        return this._querySource;
    }

    get disabled(): boolean {
        return !this.queryEditor
            || this.loading.value
            || this.queryEditor.disabled
            || this.standalone && !this.querySource.name?.trim().length;
    }

    get isDraftQuery(): boolean {
        return !!this.querySource?.isDraft;
    }

    get dirty(): boolean {
        if (this.disabled) return false;

        const editorDirty = !!this.queryEditor?.dirty;
        const filterModeChanged = this.originalFilterMode !== this.querySource.filterMode;

        return editorDirty || filterModeChanged;
    }

    get loading(): BehaviorSubject<boolean> {
        return this.queryEditor?.loading;
    }

    get isTopAdmin(): boolean {
        return this.userHelperService.isTopAdmin;
    }


    public ngOnInit(): void {
        // Refresh query sources on creation if needed.
        if (!this.standalone) {
            this.querySourceService.getAll().then(sources => {
                this.querySources = sources ?? [];
                this.refreshQuerySourceOptions();
            });
        }

        this.originalFilterMode = this.querySource.filterMode;
    }


    // Refreshing methods.
    private refreshOrganizationOptions(organizations: IOrganization[]): void {
        this.organizationOptions = organizations
            .map(organization => <SelectItem>{ value: organization, label: organization.name });
    }

    private async refreshQueryPreview(): Promise<void> {
        await this.queryPreview?.refreshGrid();
    }

    private refreshQuerySources(): void {
        // If the current query is not a draft, and it is not registered, register it.
        if (!this.isDraftQuery && !!this.querySources?.every(source => source.id !== this.querySourceId)) {
            this.querySources.push(this.querySource);
        }
        this.refreshQuerySourceOptions();
    }

    private refreshQuerySourceOptions(): void {
        let querySourceOptionsTree: TreeNode<string>[] = [];

        // If query option groups were passed, add a node for each group,
        // and place corresponding query options as its children.
        if (!!this.querySourceOptions?.length) {
            const querySourceOptions: TreeNode<string>[] = this.querySourceOptions
                .map(group => <TreeNode<string>>{
                    label: group.label,
                    expanded: true,
                    type: QuerySourceNodeType.QueryOptionGroup,
                    children: group.items.map(item => <TreeNode<string>>{
                        label: item.label ?? "<anonymous query>",
                        data: item.value,
                        type: QuerySourceNodeType.QueryOption
                    })
                });
            querySourceOptionsTree.push(...querySourceOptions);
        }

        // If there are global query options, add a "global queries" node,
        // and place global query source options as its children.
        if (!!this.querySources?.length) {
            const globalQuerySourceOptions: TreeNode<string> = {
                label: "Global queries",
                type: QuerySourceNodeType.QueryOptionGroup,
                children: this.querySources
                    .map(querySource => <TreeNode<string>>{
                        label: querySource.name ?? "<anonymous query>",
                        data: querySource.id,
                        type: QuerySourceNodeType.QueryOption
                    })
            }
            querySourceOptionsTree.push(globalQuerySourceOptions)
        }

        // If this tree has only one group node, unfold the group and
        // put all options nodes on the first level of the tree.
        if (querySourceOptionsTree.length === 1) {
            querySourceOptionsTree = querySourceOptionsTree[0].children;
        }

        this.querySourceOptionsTree = querySourceOptionsTree;
    }

    // Auxiliary methods.
    protected multiSourceOptions(): boolean {
        if (!this.querySourceOptionsTree?.length) return false;
        return this.querySourceOptionsTree.some(node => node.type === QuerySourceNodeType.QueryOptionGroup);
    }

    protected getNodeClass(node: TreeNode): string {
        if (!node) return;

        let styleClass: string = "query-option";

        if (this.isSelectedGroup(node) || this.isSelectedOption(node)) {
            styleClass += " query-option-selected";
        }

        return styleClass;
    }

    private isSelectedGroup(node: TreeNode<string>): boolean {
        return node.type === QuerySourceNodeType.QueryOptionGroup
            && node.children?.some(option => this.isSelectedOption(option));
    }

    private isSelectedOption(node: TreeNode<string>): boolean {
        return node.type === QuerySourceNodeType.QueryOption
            && node.data === this.querySourceOptionSelected;
    }

    private optionsEmpty(): boolean {
        return !this.multiSourceOptions()
            ? !this.querySourceOptionsTree?.length
            : !this.querySourceOptionsTree?.some(node => !!node.children?.length);
    }

    // Dialog methods.
    protected openQueryCreationDialog(): void {
        // If organizations are not updated yet, and this user is system admin,
        // get the organizations of this user remotely and then open the creation dialog.
        if (!this.organizationOptions && this.userHelperService.isMiProAdmin) {
            this.organizationService.getAllPlain().then(organizations => {
                this.refreshOrganizationOptions(organizations);
                this.queryCreationDialogVisible = true;
            });
        } else {
            // Otherwise, update organizations with user organizations
            // if they are not updated yet and open the creation dialog.
            if (!this.organizationOptions) {
                const user = <UserLogin>this.userService.currentUser;
                this.refreshOrganizationOptions(user?.userAccesses.map(ua => ua.organization) ?? []);
            }
            this.queryCreationDialogVisible = true;
        }
    }

    protected async createNamedQuery(name: string): Promise<void> {
        if (!name?.length) return this.messageService.add(Message.Info("Invalid query source name."));

        this.querySource.name = name;
        this.querySource.level = QueryAccessLevel.Public;

        if (!await this.createDraft()) {
            this.querySource.name = null;
            this.querySource.level = this.queryAccessLevel;
        }

        this.cancelQueryCreation()
    }

    protected cancelQueryCreation(): void {
        this.organizations = [];
        this.queryCreationDialogVisible = false;
    }

    protected openQuerySelectionDialog(): void {
        if (!this.optionsEmpty()) {
            this.querySourceOptionSelected = this.querySourceId;
            this.querySourceDialogVisible = true;

        } else this.messageService.add(Message.Info("There is no query sources to select."));
    }

    protected selectQuery(node: TreeNode<string>): void {
        if (!node?.data || node.type !== QuerySourceNodeType.QueryOption) return;
        this.querySourceOptionSelected = node.data;
    }

    protected useSelectedQuery(): void {
        this.querySourceId = this.querySourceOptionSelected;
        this.querySourceIdChange.emit(this.querySourceId);
        this.cancelQuerySelection();
    }

    protected cancelQuerySelection(): void {
        this.querySourceOptionSelected = null;
        this.querySourceDialogVisible = false;
    }

    protected async changeQueryEditionMode(): Promise<void> {
        if (this.querySource.level !== QueryAccessLevel.Local) {
            // If query source is not local, try to detach the query.
            this.querySource.name = null;
            this.querySource.id = undefined;
            this.querySource.level = QueryAccessLevel.Local;

            const querySourceId: string = await this.queryEditor.createDraft();
            if (!querySourceId) return;

            this.querySourceId = querySourceId;
            this.querySourceIdChange.emit(this.querySourceId);
            this.refreshQueryPreview()?.then();

            // Otherwise, create a new named (public) query from this query.
        } else this.openQueryCreationDialog();
    }

    // Edition methods.
    public async createDraft(): Promise<string> {
        if (this.queryEditor?.sqlQueryBuild?.querySource) {
            this.queryEditor.sqlQueryBuild.querySource.filterMode = this.querySource.filterMode;
        }

        const editionFunc = (): Promise<string> => this.queryEditor?.createDraft();
        return this.editQuery(editionFunc);
    }

    public async releaseDraft(): Promise<string> {
        if (!this.isDraftQuery) return;

        if (this.queryEditor?.sqlQueryBuild?.querySource) {
            this.queryEditor.sqlQueryBuild.querySource.filterMode = this.querySource.filterMode;
        }

        const editionFunc = (): Promise<string> => this.queryEditor?.releaseDraft();
        return this.editQuery(editionFunc);
    }

    public async cancelDraft(): Promise<void> {
        if (!this.isDraftQuery) return;

        const settings: IHttpResponseHandlerSettings = { showSuccessMessage: false };
        this.querySourceService.delete(this.querySource.id, settings).then();
    }

    public async saveDraft(): Promise<string> {
        return this.isDraftQuery ? this.save() : this.createDraft();
    }

    public async release(): Promise<string> {
        return this.isDraftQuery ? this.releaseDraft() : this.save();
    }

    public async save(): Promise<string> {
        if (this.queryEditor?.sqlQueryBuild?.querySource) {
            this.queryEditor.sqlQueryBuild.querySource.filterMode = this.querySource.filterMode;
        }

        const editionFunc = (): Promise<string> => this.queryEditor?.save();
        return this.editQuery(editionFunc);
    }

    private async editQuery(editionFunc: () => Promise<string>): Promise<string> {
        const currentFilterMode = this.querySource.filterMode;

        // Try to edit the query.
        const querySourceId: string = await editionFunc();

        // In case of error, return.
        if (!querySourceId) return null;

        if (currentFilterMode !== undefined && currentFilterMode !== null) {
            this.querySource.filterMode = currentFilterMode;
            this.originalFilterMode = currentFilterMode;
        }

        // If the query is not local check if query has attached widgets.
        if (this.querySource.level !== QueryAccessLevel.Local &&
            await this.querySourceService.hasAttachedWidgets(querySourceId, !this.standalone)) {

            this.messageService.add(Message.Warning("There are widgets connected to this query, " +
                "but as the new query schema is compatible with the old one (no columns have been lost), " +
                "the query has been updated without problems."))

        } else this.messageService.add(Message.Success(!this.querySourceId ? "Query created" : "Query updated"));

        // If query source id is the same, force query preview refreshing.
        if (this.querySourceId === querySourceId) this.refreshQueryPreview()?.then();

        // Update query source settings.
        this.querySourceId = querySourceId;
        this.querySourceIdChange.emit(this.querySourceId);
        return this.querySourceId;
    }
}